import { useEffect, useState } from "react";
import { Box, Skeleton, useTheme } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";

const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 600;

type Props = {
  src: string;
  alt: string;
  size?: number;
  onClick?: () => void;
};

/**
 * Thumbnail for a single result row.
 *
 * Renders a plain <img> instead of MUI's <Avatar>: Avatar preloads through an
 * off-DOM `new Image()` and permanently falls back to its person icon as soon
 * as that single probe fails, which is what happens when a result set of a few
 * hundred rows fires every request at once. Here the browser owns the loading
 * (lazily, so only rows near the viewport are fetched) and transient failures
 * are retried before we give up and show the fallback.
 */
export default function ResultThumbnail({
  src,
  alt,
  size = 44,
  onClick,
}: Props) {
  const theme = useTheme();
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading",
  );

  useEffect(() => {
    setAttempt(0);
    setStatus("loading");
  }, [src]);

  useEffect(() => {
    if (status !== "error" || attempt >= MAX_RETRIES) {
      return;
    }
    const timeout = setTimeout(
      () => {
        setStatus("loading");
        setAttempt((current) => current + 1);
      },
      RETRY_BASE_DELAY_MS * 2 ** attempt,
    );
    return () => clearTimeout(timeout);
  }, [status, attempt]);

  const hasFailed = status === "error" && attempt >= MAX_RETRIES;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 1.5,
        overflow: "hidden",
        cursor: onClick ? "zoom-in" : "default",
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.action.hover,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {hasFailed ? (
        <PersonIcon
          sx={{ fontSize: size * 0.6, color: theme.palette.text.disabled }}
        />
      ) : (
        <Box
          key={attempt}
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setStatus("loaded")}
          onError={() => setStatus("error")}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}

      {/* Overlaid rather than swapped in: the <img> must stay in the layout
          flow for `loading="lazy"` to ever intersect the viewport and fire. */}
      {status !== "loaded" && !hasFailed && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{ position: "absolute", inset: 0 }}
        />
      )}
    </Box>
  );
}
